// ============================================================================
// DOCUMENT SERIES (PMS Phase 2 slice 4) — statutory numbering
// ============================================================================
// One series per property × document type × financial year (the property's
// accounting entity's year). Number = PREFIX/FY/SEQUENCE, at most 16 characters.

export type DocumentType = 'TAX_INVOICE' | 'CREDIT_NOTE' | 'DEBIT_NOTE' | 'RECEIPT' | 'REFUND';

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  TAX_INVOICE: 'Tax invoice',
  CREDIT_NOTE: 'Credit note',
  DEBIT_NOTE: 'Debit note',
  RECEIPT: 'Receipt voucher',
  REFUND: 'Refund voucher',
};

export const documentTypeLabel = (v?: string | null): string =>
  (v && DOCUMENT_TYPE_LABELS[v as DocumentType]) || v || '';

/** booking.tbl_payment.receipt_number_state */
export type ReceiptNumberState = 'ISSUED' | 'PENDING' | 'EXTERNAL' | null;

export interface DocumentSeries {
  id: string;
  propertyId: string;
  propertyCode: string | null;
  propertyName: string | null;
  documentType: DocumentType;
  prefix: string;
  fyToken: string;
  financialYearId: string;
  fyCode: string | null;
  sequenceWidth: number;
  lastNumber: number;
  /** What the next document of this series will be numbered. */
  nextNumber: string;
  entityId: string;
  entityName: string | null;
  rowVersion: number;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface UpdateDocumentSeriesRequest {
  prefix: string;
  sequenceWidth: number;
  rowVersion: number;
}

export interface DocumentSeriesGapReport {
  seriesId: string;
  lastNumber: number;
  issuedCount: number;
  missingSequences: number[];
}

export interface PendingNumbersSummary {
  propertyId: string;
  pendingCount: number;
  /** Why numbering cannot happen now; null when it can. */
  blockingReason: string | null;
}

// ---- running (non-statutory) series — GRC-1 ------------------------------------------
// One per property × type; no financial-year reset, no year code. Number = PREFIX + SEQUENCE
// (zero-padded to the digits; 0 = no padding). A hotel moving from a paper GRC pad sets an
// empty prefix and the pad's next number as the start.

export type RunningSeriesType = 'GRC';

export const RUNNING_SERIES_LABELS: Record<RunningSeriesType, string> = {
  GRC: 'Guest registration card',
};

export const runningSeriesLabel = (v?: string | null): string =>
  (v && RUNNING_SERIES_LABELS[v as RunningSeriesType]) || v || '';

export interface RunningSeries {
  id: string;
  propertyId: string;
  propertyCode: string | null;
  propertyName: string | null;
  seriesType: RunningSeriesType;
  prefix: string;
  sequenceWidth: number;
  startNumber: number;
  lastNumber: number;
  issuedCount: number;
  /** What the next number will be (numbers already recorded are skipped when issuing). */
  nextNumber: string;
  rowVersion: number;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface UpdateRunningSeriesRequest {
  prefix: string;
  sequenceWidth: number;
  startNumber: number;
  rowVersion: number;
}

export interface IssuePendingResult {
  issued: number;
  stillPending: number;
  reason: string | null;
}
